import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";

import ConfirmationModal from "../modals/ConfirmationModal";
import { useDeleteBriefingMutation } from "../../store/apis/briefingApi";
import { setToastMessage } from "../../store/slices/app/appSlice";

interface DeleteBriefingDialogProps {
  briefingId: string;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}

/** Confirm-and-delete dialog for a briefing, usable from any trigger
 *  (an icon button, a menu item…). */
const DeleteBriefingDialog: React.FC<DeleteBriefingDialogProps> = ({
  briefingId,
  open,
  onClose,
  onDeleted,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [deleteBriefing, { isLoading: isDeleting }] =
    useDeleteBriefingMutation();

  const handleConfirm = async () => {
    try {
      await deleteBriefing({ id: briefingId }).unwrap();

      dispatch(
        setToastMessage({
          message: t(
            "briefings.deleteSuccess",
            "Briefing deleted successfully"
          ),
          severity: "success",
        })
      );
      onDeleted?.();
    } catch (error) {
      Sentry.captureException(error);
      dispatch(
        setToastMessage({
          message: t("briefings.deleteError", {
            defaultValue: "Unable to delete briefing",
            error: error instanceof Error ? error.message : "Unknown error",
          }),
          severity: "error",
        })
      );
    } finally {
      onClose();
    }
  };

  return (
    <ConfirmationModal
      open={open}
      title={t("briefings.deleteConfirmTitle", "Delete briefing")}
      message={t(
        "briefings.deleteConfirmMessage",
        "Are you sure you want to delete this briefing? This action cannot be undone."
      )}
      onConfirm={handleConfirm}
      onCancel={onClose}
      isLoading={isDeleting}
    />
  );
};

export default DeleteBriefingDialog;
